import { FunctionComponent, ReactNode, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";

import { API_URL } from "../../config";
import {
  formatDateTimeValue,
  mapDocumentsToDownloadItems,
} from "./opsPpsDownloads";
import useOpsPpsDetails from "../../hooks/useOpsPpsDetails";
import { OpsFlightNoteDialog } from "./OpsFlightNoteDialog";

type OpsPpsHighlight = {
  topic: string;
  subtopic?: string | null;
  change_type?: string | null;
  paths?: string[];
  old_value?: string | number | null;
  new_value?: string | number | null;
  delta?: number | null;
  delta_pct?: number | null;
  severity?: "info" | "warning" | "high" | "critical";
  message?: string | null;
  items_added?: string[];
  items_removed?: string[];
  reordered?: boolean;
  details?: OpsPpsHighlight[];
};

type OpsPpsKeyFact = {
  key: string;
  label: string;
  value?: string | number | null;
  previousValue?: string | number | null;
  delta?: number | null;
  deltaText?: string | null;
  deltaUnit?: string | null;
};

const formatCtotStatus = (
  status: OpsPpsInfo["ctotStatus"] | OpsPpsDetailsResponse["ctotStatus"]
) => {
  switch (status) {
    case "assigned":
      return "Slot assigned";
    case "not_assigned":
      return "No slot assigned";
    default:
      return "Unknown";
  }
};

const Section: FunctionComponent<{ title: string; children: ReactNode }> = ({
  title,
  children,
}) => (
  <Box
    sx={{
      borderRadius: 3,
      border: (theme) => `1px solid ${theme.palette.divider}`,
      p: { xs: 2, md: 3 },
      backgroundColor: (theme) => theme.palette.background.paper,
    }}
  >
    <Stack spacing={2}>
      <Typography variant="h6">{title}</Typography>
      {children}
    </Stack>
  </Box>
);

const renderLabelValue = (label: string, value: ReactNode) => (
  <Grid item xs={12} sm={6} md={4} key={label}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ mt: 0.5 }}>
      {value || "—"}
    </Typography>
  </Grid>
);

type Props = {
  flight: OpsFlight | null;
  open: boolean;
  onClose: () => void;
};

export const OpsPpsOverlay: FunctionComponent<Props> = ({
  flight,
  open,
  onClose,
}) => {
  const operatorId = flight?.operatorId ?? null;
  const ppsFlightId = flight?.pps?.flightId ?? null;
  const ppsGufi = flight?.pps?.gufi ?? null;
  const ppsPublishedAt =
    flight?.pps?.lastEdit ||
    flight?.pps?.latestFlightPlanDate ||
    flight?.pps?.latestDocumentUploadDate ||
    null;
  const ppsFlightLogId = flight?.pps?.flightLogId ?? null;

  const { data, isLoading, isError } = useOpsPpsDetails(
    operatorId,
    ppsFlightId,
    open
  );
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [highlights, setHighlights] = useState<OpsPpsHighlight[]>([]);
  const [keyFacts, setKeyFacts] = useState<OpsPpsKeyFact[]>([]);
  const [highlightsLoading, setHighlightsLoading] = useState(false);
  const [highlightsError, setHighlightsError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setIsNoteDialogOpen(false);
      setHighlights([]);
      setKeyFacts([]);
      setHighlightsError(null);
      setHighlightsLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setHighlights([]);
      setKeyFacts([]);
      setHighlightsLoading(false);
      setHighlightsError(null);
      return;
    }

    if (!operatorId || !ppsFlightId || !ppsGufi) {
      setHighlights([]);
      setKeyFacts([]);
      setHighlightsLoading(false);
      setHighlightsError(null);
      return;
    }

    const controller = new AbortController();
    const params = new URLSearchParams({ gufi: ppsGufi });
    if (ppsPublishedAt) {
      params.set("publishedAt", ppsPublishedAt);
    }
    if (ppsFlightLogId) {
      params.set("flightLogId", ppsFlightLogId);
    }

    setHighlightsLoading(true);
    setHighlightsError(null);

    const fetchHighlights = async () => {
      try {
        const response = await fetch(
          `${API_URL}/ops/pps/operators/${operatorId}/flights/${ppsFlightId}/briefing-diff?${params.toString()}`,
          { signal: controller.signal, credentials: "include" },
        );

        if (!response.ok) {
          if (response.status === 404) {
            setHighlights([]);
            return;
          }
          throw new Error("Unable to load briefing highlights");
        }

        const payload = await response.json();
        const payloadHighlights = Array.isArray(payload.highlights)
          ? payload.highlights
          : [];
        const payloadKeyFacts = Array.isArray(payload.keyFacts)
          ? payload.keyFacts
          : [];
        setHighlights(payloadHighlights);
        setKeyFacts(payloadKeyFacts);
      } catch (err) {
        if (!controller.signal.aborted) {
          setHighlightsError(
            err instanceof Error ? err.message : "Unable to load briefing highlights",
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setHighlightsLoading(false);
        }
      }
    };

    fetchHighlights();

    return () => controller.abort();
  }, [
    open,
    operatorId,
    ppsFlightId,
    ppsGufi,
    ppsPublishedAt,
    ppsFlightLogId,
  ]);

  const atc = data?.atc || flight?.pps?.atc || null;
  const documents = useMemo(
    () => data?.documents || flight?.pps?.documents || [],
    [data?.documents, flight?.pps?.documents]
  );
  const documentItems = useMemo(
    () => mapDocumentsToDownloadItems(documents, operatorId),
    [documents, operatorId],
  );
  const airports = data?.airports || flight?.pps?.airports || null;
  const ctotStatus = data?.ctotStatus || flight?.pps?.ctotStatus || "unknown";

  const airportSections = useMemo(
    () => {
      const entries: Array<[string, OpsPpsAirportInfo | null]> = [
        ["Departure", airports?.departure || null],
        ["Destination", airports?.destination || null],
      ];

      if (airports?.alternate1 || airports?.alternate2) {
        entries.push(["Alternate 1", airports?.alternate1 || null]);
        entries.push(["Alternate 2", airports?.alternate2 || null]);
      }

      return entries;
    },
    [airports]
  );

  const chartsHref =
    operatorId !== null && ppsFlightId
      ? `${API_URL}/ops/pps/operators/${operatorId}/flights/${ppsFlightId}/charts`
      : null;

  const handleOpenNoteDialog = () => {
    setIsNoteDialogOpen(true);
  };

  const handleCloseNoteDialog = () => {
    setIsNoteDialogOpen(false);
  };

  const summaryItems = [
    renderLabelValue("Flight ID", flight?.flightID || "—"),
    renderLabelValue("Callsign", flight?.flightIdentifier || "—"),
    renderLabelValue("Route", `${flight?.departureAirport || "—"} → ${
      flight?.arrivalAirport || "—"
    }`),
    renderLabelValue("Aircraft", flight?.aircraftRegistration || flight?.pps?.tail || "—"),
    renderLabelValue("STD", formatDateTimeValue(flight?.pps?.std)),
    renderLabelValue("STA", formatDateTimeValue(flight?.pps?.sta)),
    renderLabelValue("ETA", formatDateTimeValue(flight?.pps?.eta)),
    renderLabelValue("ATC DOF", flight?.pps?.atcDof || "—"),
    renderLabelValue("Release", flight?.pps?.releasedForDispatch ? "Released" : "Pending"),
  ];

  const getSeverityColor = (severity?: OpsPpsHighlight["severity"]) => {
    switch (severity) {
      case "critical":
        return "error";
      case "high":
        return "warning";
      case "warning":
        return "warning";
      case "info":
      default:
        return "info";
    }
  };

  const renderKeyFacts = (facts: OpsPpsKeyFact[]) => (
    <Stack spacing={1.5}>
      {facts.map((fact) => {
        const value = fact.value ?? "—";
        const previousValue =
          fact.previousValue !== null && fact.previousValue !== undefined
            ? fact.previousValue
            : null;
        const deltaValue =
          fact.delta !== null && fact.delta !== undefined
            ? `${fact.delta > 0 ? "+" : ""}${fact.delta}${
              fact.deltaUnit ? ` ${fact.deltaUnit}` : ""
            }`
            : null;
        const deltaText = fact.deltaText || deltaValue;
        const showDelta = previousValue !== null && value !== previousValue;

        return (
          <Box
            key={fact.key}
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              backgroundColor: (theme) => theme.palette.background.paper,
            }}
          >
            <Typography variant="subtitle2">{fact.label}</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {value}
            </Typography>
            {showDelta && (
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Previous: {previousValue}
                </Typography>
                {deltaText && (
                  <Typography variant="caption" color="text.secondary">
                    Δ {deltaText}
                  </Typography>
                )}
              </Stack>
            )}
          </Box>
        );
      })}
    </Stack>
  );

  const renderHighlightDetails = (details: OpsPpsHighlight[]) => (
    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
      {details.map((detail, index) => (
        <Stack
          key={`${detail.topic}-${detail.subtopic}-${index}`}
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
        >
          <Typography variant="caption" color="text.secondary">
            {detail.subtopic || detail.topic}
          </Typography>
          <Typography variant="body2">{detail.message || "Updated"}</Typography>
        </Stack>
      ))}
    </Stack>
  );

  const atcItems = [
    renderLabelValue("CTOT", atc?.ctot || flight?.pps?.atcCtot || "—"),
    renderLabelValue("ATC Time", atc?.time || flight?.pps?.atcTime || "—"),
    renderLabelValue("ATC Rule", atc?.rule || "—"),
    renderLabelValue("ATC Type", atc?.type || "—"),
    renderLabelValue("ATC Number", atc?.number || "—"),
    renderLabelValue("ATC Route", atc?.route || "—"),
    renderLabelValue("Destination", atc?.destination || flight?.pps?.arrival || "—"),
    renderLabelValue("EET", atc?.eet || flight?.pps?.atcEet || "—"),
    renderLabelValue("PIC", atc?.pic || "—"),
  ];

  const sortedHighlights = useMemo(() => {
    if (!highlights) {
      return [];
    }
    const order = {
      critical: 0,
      high: 1,
      warning: 2,
      info: 3,
      undefined: 4,
    } as const;
    return [...highlights].sort((a, b) => {
      const aKey = (a.severity ?? "undefined") as keyof typeof order;
      const bKey = (b.severity ?? "undefined") as keyof typeof order;
      if (order[aKey] !== order[bKey]) {
        return order[aKey] - order[bKey];
      }
      return a.topic.localeCompare(b.topic);
    });
  }, [highlights]);

const renderAirport = (label: string, airport: OpsPpsAirportInfo | null) => (
  <Box
    sx={{
      borderRadius: 2,
      border: (theme) => `1px solid ${theme.palette.divider}`,
      p: { xs: 1.5, md: 2 },
      backgroundColor: (theme) => theme.palette.background.paper,
    }}
  >
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {label}
    </Typography>
    {!airport ? (
      <Typography variant="body2" color="text.secondary">
        No data available.
      </Typography>
    ) : (
      <Stack spacing={1}>
        <Typography variant="body2">
          {airport.name || airport.icao || "—"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {[
            airport.icao,
            airport.iata,
            airport.country,
          ]
            .filter(Boolean)
            .join(" · ") || "—"}
        </Typography>
        {airport.metar && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              METAR
            </Typography>
            <Typography variant="body2">{airport.metar}</Typography>
          </Box>
        )}
        {airport.taf?.text && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              TAF
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {airport.taf.text}
            </Typography>
          </Box>
        )}
        {!!airport.runways.length && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Runways
            </Typography>
            <Typography variant="body2">
              {airport.runways
                .map((runway) =>
                  runway.length
                    ? `${runway.name || "—"} (${runway.length})`
                    : runway.name || "—"
                )
                .join(", ")}
            </Typography>
          </Box>
        )}
      </Stack>
    )}
  </Box>
);

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="xl"
        open={open}
        onClose={onClose}
        PaperProps={{
        sx: {
          width: { xs: "95%", lg: "80%" },
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>
        {flight?.flightIdentifier || flight?.flightID || "Flight details"}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          backgroundColor: (theme) => theme.palette.background.default,
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 3 },
        }}
      >
        <Stack spacing={3}>
          <Section title="Flight overview">
            <Grid container spacing={2}>
              {summaryItems}
            </Grid>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" fontWeight={600}>
                CTOT status:
              </Typography>
              <Chip
                size="small"
                label={formatCtotStatus(ctotStatus)}
                color={ctotStatus === "assigned" ? "success" : "default"}
              />
            </Stack>
          </Section>

          <Section title="Briefing highlights">
            {highlightsLoading && (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Loading briefing highlights…
                </Typography>
              </Stack>
            )}
            {!highlightsLoading && highlightsError && (
              <Alert severity="warning">{highlightsError}</Alert>
            )}
            {!highlightsLoading &&
              !highlightsError &&
              highlights.length === 0 &&
              (keyFacts.length ? (
                renderKeyFacts(keyFacts)
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No highlights available for the current briefing.
                </Typography>
              ))}
            {!highlightsLoading && !highlightsError && highlights.length > 0 && (
              <Stack spacing={1.5}>
                {highlights.map((highlight, index) => (
                  <Box
                    key={`${highlight.topic}-${highlight.subtopic || index}`}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      backgroundColor: (theme) => theme.palette.background.paper,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle2">{highlight.topic}</Typography>
                      {highlight.severity && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: `${getSeverityColor(highlight.severity)}.main`,
                            textTransform: "uppercase",
                          }}
                        >
                          {highlight.severity}
                        </Typography>
                      )}
                    </Stack>
                    {highlight.subtopic && (
                      <Typography variant="caption" color="text.secondary">
                        {highlight.subtopic}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {highlight.message || "Updated"}
                    </Typography>
                    {(highlight.items_added?.length ||
                      highlight.items_removed?.length ||
                      highlight.reordered) && (
                      <Typography variant="caption" color="text.secondary">
                        {highlight.items_added?.length
                          ? `Added: ${highlight.items_added.join(", ")} `
                          : ""}
                        {highlight.items_removed?.length
                          ? `Removed: ${highlight.items_removed.join(", ")} `
                          : ""}
                        {highlight.reordered ? "Order changed" : ""}
                      </Typography>
                    )}
                    {highlight.details && highlight.details.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Details
                        </Typography>
                        {renderHighlightDetails(highlight.details)}
                      </Box>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Section>

          <Section title="ATC & slot information">
            {isLoading && (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Loading PPS data…
                </Typography>
              </Stack>
            )}
            {isError && (
              <Alert severity="warning">
                Unable to load PPS details right now. Please try again later.
              </Alert>
            )}
            {!isLoading && !isError && (
              <Grid container spacing={2}>
                {atcItems}
              </Grid>
            )}
          </Section>

          <Section title="Highlights">
            {highlightsLoading && (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Loading highlights…
                </Typography>
              </Stack>
            )}
            {!highlightsLoading && highlightsError && (
              <Alert severity="warning">{highlightsError}</Alert>
            )}
            {!highlightsLoading && !highlightsError && (
              <Stack spacing={1.5}>
                {sortedHighlights.length ? (
                  sortedHighlights.map((highlight, index) => (
                    <Box
                      key={`${highlight.topic}-${highlight.subtopic || index}`}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        border: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {highlight.topic}
                        </Typography>
                        {highlight.severity && (
                          <Typography
                            variant="caption"
                            sx={{ color: `${getSeverityColor(highlight.severity)}.main` }}
                          >
                            {highlight.severity.toUpperCase()}
                          </Typography>
                        )}
                      </Stack>
                      {highlight.subtopic && (
                        <Typography variant="caption" color="text.secondary">
                          {highlight.subtopic}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {highlight.message || "Updated"}
                      </Typography>
                      {(highlight.items_added?.length ||
                        highlight.items_removed?.length ||
                        highlight.reordered) && (
                        <Typography variant="caption" color="text.secondary">
                          {highlight.items_added?.length
                            ? `Added: ${highlight.items_added.join(", ")} `
                            : ""}
                          {highlight.items_removed?.length
                            ? `Removed: ${highlight.items_removed.join(", ")} `
                            : ""}
                          {highlight.reordered ? "Order changed" : ""}
                        </Typography>
                      )}
                      {highlight.details && highlight.details.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Details
                          </Typography>
                          {renderHighlightDetails(highlight.details)}
                        </Box>
                      )}
                    </Box>
                  ))
                ) : keyFacts.length ? (
                  renderKeyFacts(keyFacts)
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No highlights available.
                  </Typography>
                )}
              </Stack>
            )}
          </Section>

          <Section title="Airports & weather">
            {isLoading && (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Loading airport data…
                </Typography>
              </Stack>
            )}
            {!isLoading && (
              <Stack
                spacing={2}
                direction={{ xs: "column", md: "row" }}
                useFlexGap
                flexWrap="wrap"
              >
                {airportSections.map(([label, info]) => (
                  <Box
                    key={label}
                    sx={{
                      flex: { md: "1 1 45%" },
                      minWidth: { md: 280 },
                    }}
                  >
                    {renderAirport(label, info)}
                  </Box>
                ))}
              </Stack>
            )}
          </Section>

          <Section title="Documents & downloads">
            {!documentItems.length ? (
              <Typography variant="body2" color="text.secondary">
                No PPS documents available for download.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {documentItems.map((document) => (
                  <Stack
                    key={document.key}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <DescriptionIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2">{document.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {document.meta}
                        </Typography>
                      </Box>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      component={document.href ? "a" : "button"}
                      href={document.href || undefined}
                      target={document.href ? "_blank" : undefined}
                      rel={document.href ? "noopener" : undefined}
                      disabled={!document.href}
                    >
                      Download
                    </Button>
                  </Stack>
                ))}
              </Stack>
            )}
            <Divider sx={{ my: 1.5 }} />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
              <Typography variant="body2" fontWeight={600}>
                Default charts package
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                component={chartsHref ? "a" : "button"}
                href={chartsHref || undefined}
                target={chartsHref ? "_blank" : undefined}
                rel={chartsHref ? "noopener" : undefined}
                disabled={!chartsHref}
              >
                Download charts PDF
              </Button>
            </Stack>
          </Section>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={handleOpenNoteDialog}
          disabled={!flight?.flightID}
        >
          Post ops note
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      </Dialog>
      <OpsFlightNoteDialog
        flight={flight}
        open={isNoteDialogOpen}
        onClose={handleCloseNoteDialog}
      />
    </>
  );
};
